import React, { useState, useContext, FC, useEffect, useCallback } from "react"
import { Table, Input, Space, Button } from "antd"
import type { ColumnsType, TableProps } from "antd/es/table"
import { SearchOutlined } from "@ant-design/icons"
import { AllUserDataType } from "../types/DataType"
import { SidebarContext, isAuth } from "../../../auth/ProtectedRoute"
import axios from "axios"
import moment from "moment"
import SkeletonTable, { SkeletonTableColumnsType } from "../../../../components/SkeletonTable"
import { FilterConfirmProps, FilterDropdownProps } from "antd/es/table/interface"
import { CircularProgress, Box } from "@mui/material";



const AllUserTable: FC<{ getCount?: (val: number) => void }> = (props) => {
  const { userName, sidebarData } = useContext(SidebarContext)
  const [loading, setLoading] = useState<boolean>(false)
  const [userTableData, setUserTableData] = useState<AllUserDataType[]>([])
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  })
  const [filters, setFilters] = useState<{ name?: string; email?: string }>({})
  const [sortField, setSortField] = useState<string | null>(null)
  const [sortOrder, setSortOrder] = useState<"ascend" | "descend" | null>(null)

  const fetchData = useCallback(async (params: any = {}) => {
    setLoading(true)
    try {
      const { current, pageSize, filters, sorter } = params
      let url = `${process.env.REACT_APP_HOST_API}manage-user/list?type=all`
      url += `&page=${current}&per_page=${pageSize}`

      if (filters?.name) {
        url += `&fullname=${encodeURIComponent(filters.name[0])}`
      }
      if (filters?.email) {
        url += `&email=${encodeURIComponent(filters.email[0])}`
      }
      if (sorter?.field) {
        url += `&sort_by=${sorter.field}&sort_order=${sorter.order === "ascend" ? "asc" : "desc"}`
      }

      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${isAuth()}`,
        },
      })

      const { posts, pagination: paginationData } = response.data.data

      setUserTableData(
        posts.map((userData: any) => ({
          key: userData.id,
          project_id: userData.id,
          photo: process.env.REACT_APP_IMAGE_API_PREFIX + userData.photo,
          name: userData.name,
          email: userData.email,
          location: userData.location,
          date_registered: userData.date_registered,
          last_login_date: userData.last_login_date,
        }))
      )

      setPagination({
        ...params.pagination,
        total: paginationData.total,
      })
      if (props.getCount) props.getCount(paginationData.total)
    } catch (error) {
      console.error("Error fetching data:", error)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchData({ current: 1, pageSize: 10 })
  }, [fetchData])

  const handleTableChange: TableProps<AllUserDataType>["onChange"] = (
    pagination,
    filters,
    sorter
  ) => {
    fetchData({
      current: pagination.current,
      pageSize: pagination.pageSize,
      filters,
      sorter,
    })
  }

  const getColumnSearchProps = (dataIndex: keyof AllUserDataType) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }: FilterDropdownProps) => (
      <div style={{ padding: 8 }}>
        <Input
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => confirm()}
          style={{ width: 188, marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => confirm()}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button onClick={() => clearFilters?.()} size="small" style={{ width: 90 }}>
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
    ),
    onFilter: (value: string | number | boolean, record: AllUserDataType) =>
      record[dataIndex]?.toString().toLowerCase().includes(value.toString().toLowerCase()) ?? false,
  })
  const columns: ColumnsType<AllUserDataType> = [
    {
      title: "Name",
      dataIndex: "name",
      ...getColumnSearchProps("name"),
      sorter: true,
      render: (text, record) => (
        <div className="user-area" style={{ alignItems: "center", display: "flex" }}>
          {record.photo && !record.photo.endsWith('null') && !record.photo.endsWith('undefined') ? (
            <div style={{ width: "45px", height: "45px", marginRight: "20px", flexShrink: 0 }}>
              <img
                src={record.photo}
                alt={text}
                style={{ width: "100%", height: "100%", borderRadius: "50%", objectFit: "cover" }}
                onError={(e) => {
                   (e.target as HTMLImageElement).style.display = 'none';
                   (e.target as HTMLImageElement).parentElement!.innerHTML = `
                    <div style="width: 45px; height: 45px; border-radius: 50%; background-color: #4895ef; display: flex; justify-content: center; align-items: center; color: #fff; font-weight: 600; font-size: 16px;">
                      ${text?.charAt(0)?.toUpperCase() || "?"}
                    </div>
                   `;
                }}
              />
            </div>
          ) : (
            <div
              style={{
                width: "45px",
                height: "45px",
                borderRadius: "50%",
                backgroundColor: "#4895ef",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                color: "#fff",
                fontWeight: 600,
                fontSize: "16px",
                marginRight: "20px",
                flexShrink: 0,
              }}
            >
              {text?.charAt(0)?.toUpperCase() || "?"}
            </div>
          )}
          <p className="user_name" style={{ margin: 0, fontWeight: 500 }}>
            {text}
          </p>
        </div>
      ),
    },
    {
      title: "Email",
      dataIndex: "email",
      ...getColumnSearchProps("email"),
      sorter: true,
    },
    {
      title: "Location",
      dataIndex: "location",
    },
    {
      title: "Date Registered",
      dataIndex: "date_registered",
      render: date_registered => moment(date_registered).format("MM/DD/YYYY"),
      sorter: true,
    },
    {
      title: "Last Login Date",
      dataIndex: "last_login_date",
      render: last_login_date => moment(last_login_date).format("MM/DD/YYYY"),
      sorter: true,
    },
  ]

  return (
    <div>
      <SkeletonTable loading={loading} columns={columns as SkeletonTableColumnsType[]}>
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "200px" }}>
          <CircularProgress size={40} thickness={4} style={{ color: "#4895ef" }} />
        </Box>
      ) : (
      <Table
          columns={columns}
          dataSource={userTableData}
          pagination={pagination}
          onChange={handleTableChange}
        />
      )}
    </SkeletonTable>
    </div>
  )
}

export default AllUserTable
